import React from "react";
import { sanitizeInputElement } from "@/util/ElementSanitation";
import { sanitizeName } from "@/realize-shared/Sanitization";
import { MIN_NAME_LENGTH, MAX_NAME_LENGTH } from "@/data/Config";
import { getReadableId } from "@/util/ReadableID";
import { useAppContext } from "@/game/AppContext";

export interface NameEntryRef
{
    value: string | {
        error: string;
    };
}

const WARN = 0.3;
const BLOCK_ON_MAX = true as boolean;


const NameEntry = React.forwardRef(
    function NameEntry(
        props: { username?: string; },
        ref: React.ForwardedRef<NameEntryRef>,
    )
    {
        /**
         * @note
         *
         * So there is a problem with char counts. js does not see emojis as a single
         * char (because they are not), but people do.
         *
         * One can get the perceived char count as such:
         *
         *     const test = "equal rights 👩🏼‍❤️‍👩🏾 🏳️‍🌈 🏳️‍⚧️";
         *
         *     const Seg = new Intl.Segmenter();
         *     const chars = (s: string) => [...Seg.segment(s)].map(s=>s.segment);
         *
         *     const correct = chars(test).length; // 18
         *     const incorrect = test.length; // 39
         *
         * The thing is, cursor positioning does not support these 'wide' chars, so
         * we need to roll our own, and it's error prone. For now, I think it's best
         * ignore the issue and to just not show character count until near the limit.
         *
         * @update
         *
         * on second thought, I think this is a non-issue, since trim can happen only on submit
         */

        const { humanID } = useAppContext();
        const { username } = props;
        const [ focused, setFocused ] = React.useState(false);
        const [ value, setValue ] = React.useState(username ?? "");
        const [ output, setOutput ] = React.useState<NameEntryRef["value"]>(value);
        const [ remaining, setRemaining ] = React.useState(value.length);
        const placeholder = React.useMemo(() => getReadableId(humanID, MAX_NAME_LENGTH), [humanID]);

        const chars = React.useMemo(() =>
        {
            if ("Segmenter" in Intl)
            {
                const Seg = new Intl.Segmenter();

                return (s: string) => [...Seg.segment(s)].map(s => s.segment);
            }
            else
            {
                return (s: string) => s.split("");
            }
        }, []);

        React.useEffect(() =>
        {
            const length = chars(value).length;

            const output = (length === 0)
                ? placeholder
                : (length > MAX_NAME_LENGTH)
                    ? { error: "Username too long" }
                    : (length < MIN_NAME_LENGTH)
                        ? { error: "Username too short" }
                        : value;

            setRemaining(MAX_NAME_LENGTH - length);
            setOutput(output);
        }, [chars, placeholder, value]);

        React.useImperativeHandle(ref, () => ({
            value: output,
        }), [output]);

        return (
            <div
                className="flex-grow relative -top-2"
                id="nickname-input"
            >
                <div className="flex text-opacity-[0.72] text-white font-default leading-6 gap-[10px]">
                    <span>
                        Nickname
                    </span>
                    { focused && remaining < MAX_NAME_LENGTH*WARN && (
                        <span
                            className={`
                                text-xs
                                leading-6
                                ${remaining <= 0? "text-rose-700": "text-amber-600"}
                            `}
                        >
                            {remaining}
                        </span>
                    )}
                </div>
                <div>
                    <input
                        type="text"
                        className="
                            bg-transparent
                            text-white
                            w-full
                            border-b-2
                            border-white
                            border-opacity-20
                            font-default
                            font-bold
                            focus:border-opacity-100
                            focus:outline-none
                        "
                        placeholder={placeholder}
                        maxLength={BLOCK_ON_MAX? MAX_NAME_LENGTH: undefined}
                        value={value}
                        onFocus={() => void setFocused(true)}
                        onBlur={() => void setFocused(false)}
                        onChange={(e) =>
                        {
                            setValue(sanitizeInputElement(
                                e.currentTarget,
                                sanitizeName,
                            ));
                        }}
                    />
                </div>
            </div>
        );
    }
);

export default NameEntry;
